/**
 * Scene/layer schema for the shared image editor. See
 * IMAGE_EDITOR_REQUIREMENTS.md section 6 for the design rationale.
 *
 * This schema is only ever used inside the editor and, optionally, by a host
 * page that wants to persist it for re-editing later. It is never required by
 * anything that just wants to *display* the result — that's always a plain
 * flattened PNG/JPG, per the "no custom parsing engine" requirement.
 */

export type EditorLayerType =
  | 'background-image'
  | 'photo'
  | 'logo'
  | 'text'
  | 'qr'
  | 'decoration';

/**
 * Shapes offered for 'decoration' layers. 'ellipse' is a legacy alias kept
 * for backward compatibility with scenes saved before the shape set was
 * expanded — ImageEditorCanvasService treats it the same as 'circle'.
 */
export type ShapeType =
  | 'rect'
  | 'circle'
  | 'triangle'
  | 'diamond'
  | 'pentagon'
  | 'hexagon'
  | 'star'
  | 'heart'
  | 'arrow'
  | 'speech-bubble'
  | 'ellipse';

/**
 * Named painterly "Brush" texture presets, selectable wherever fillMode (or
 * strokeMode) is 'brush' — shape fill, shape border, and text color. Each id
 * maps to a distinct stroke-generation recipe in
 * ImageEditorCanvasService.paintBrushTile (density, stroke width/shape,
 * opacity range). Labels are Photoshop-gouache-brush-inspired.
 */
export interface BrushStyleOption {
  id: string;
  label: string;
}

export const BRUSH_STYLES: BrushStyleOption[] = [
  { id: 'dryBristle', label: 'Dry Bristle' },
  { id: 'wetBristle', label: 'Wet Bristle' },
  { id: 'pointillist', label: 'Pointillist' },
  { id: 'smoothStroke', label: 'Smooth Stroke' },
  { id: 'grainyGouache', label: 'Grainy Gouache' },
  { id: 'onPaper', label: 'On Paper' },
];

export const DEFAULT_BRUSH_STYLE = BRUSH_STYLES[0].id;

export interface EditorLayer {
  id: string;
  type: EditorLayerType;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  zIndex: number;
  /**
   * Type-specific payload. Every layer type can additionally carry the
   * generic shadow props (shadowEnabled, shadowColor, shadowBlur,
   * shadowOffsetX, shadowOffsetY). Examples:
   * - 'text': { text, fontFamily, fontSize, color, align, bold, italic,
   *   backgroundColor, curve, textStrokeEnabled, textStrokeColor,
   *   textStrokeWidth } — curve is -100..100, 0 = straight IText, non-zero
   *   bakes the text into a Group of characters arranged on an arc.
   * - 'background-image' | 'photo' | 'logo': { sourceUrl } — the left rail
   *   only exposes a single generic "Photo" tool that adds 'photo' layers;
   *   'background-image'/'logo' still exist for AI-driven / programmatic use
   *   (e.g. EditorAiCommand's 'set-background', which auto-covers + sends to
   *   back) and for backward compatibility with previously-saved scenes.
   * - 'decoration': { shapeType, fillMode ('solid'|'gradient'|'brush'), fill,
   *   fillOpacity (0-100), fillGradientStops ({offset0-100,color}[]),
   *   brushStyle (BRUSH_STYLES id, applies when fillMode === 'brush'),
   *   strokeStyle ('none'|'solid'|'dashed'|'dotted'), strokeMode
   *   ('solid'|'gradient'|'brush'), stroke, strokeWidth, strokeGradientStops
   *   ({offset0-100,color}[]), strokeBrushStyle (BRUSH_STYLES id, applies
   *   when strokeMode === 'brush'), blurType ('none'|'linear'|'radial'),
   *   blurAmount }
   * - 'qr': { targetUrl }
   */
  props: Record<string, any>;
  /**
   * Populated only for a grouped layer (type 'decoration', props.shapeType
   * === 'group', created via multi-select → Group) — a display-only summary
   * of the objects folded into it, for the Layers panel's indented child
   * rows. x/y here are in the GROUP's own local coordinate space (Fabric
   * Group children are positioned relative to the group, not the artboard),
   * so treat these as read-only labels/icons, not editable positions.
   * NOTE: children are not currently round-tripped through Save/reload — a
   * saved-and-reopened scene loses grouping (falls back to a plain shape).
   */
  children?: EditorLayer[];
}

export interface EditorScene {
  version: 1;
  aspectRatio: '3:4' | '1:1' | '9:16';
  layers: EditorLayer[];
}

/**
 * A short, AI-context-friendly summary of a layer — used to ground chat
 * intents like "make the title bigger" in a real layerId. Returned by
 * ImageEditorComponent.getLayerSummaries(); never persisted.
 */
export interface LayerSummary {
  id: string;
  type: EditorLayerType;
  label: string;
}

/**
 * The command surface a host page's AI intent router calls into once it has
 * decided *what* the user wants done to the image. This is the boundary
 * between "AI understood the request" (host page's job, via
 * NoticeService.classifyNoticeIntent or equivalent) and "the edit actually
 * happened" (this component's job, via ImageEditorCanvasService). Keeping
 * this as a small typed union — rather than letting host pages poke at
 * layers directly — is what keeps the editor pluggable across notice/event/
 * future pages: every host speaks this same command language.
 */
export type EditorAiCommand =
  | { type: 'set-background'; sourceUrl: string }
  | { type: 'update-layer'; layerId: string; props: Record<string, any> }
  | { type: 'add-layer'; layerType: EditorLayerType; props: Record<string, any> }
  | { type: 'remove-layer'; layerId: string };
